import {
  AmountType,
  CustomFeeAmountType,
  LeaseChargeType,
  SystemTranslationKeys,
} from '@common/entities';
import { isPositiveAmount } from '@common/validation-schemas';
import { CartCharge } from 'state/slices/shoppingCartSlice';

import { formatCurrency } from 'utils/format';
import { isInstallmentCharge } from 'utils/installment-charges';

type ChargeDetailTextType = 'frequency' | 'timing' | 'method' | 'requirement';

const CHARGE_DETAIL_TRANSLATION_KEYS: Record<ChargeDetailTextType, Record<string, string>> = {
  frequency: {
    'per hour': SystemTranslationKeys.cart.chargeFrequency.perHour,
    'per day': SystemTranslationKeys.cart.chargeFrequency.perDay,
    'per quarter': SystemTranslationKeys.cart.chargeFrequency.perQuarter,
    'per month': SystemTranslationKeys.cart.chargeFrequency.perMonth,
    'per occurrence': SystemTranslationKeys.cart.chargeFrequency.perOccurrence,
    'one-time': SystemTranslationKeys.cart.chargeFrequency.oneTime,
    'per year': SystemTranslationKeys.cart.chargeFrequency.perYear,
    'per installment': SystemTranslationKeys.cart.chargeFrequency.perInstallment,
  },
  timing: {
    'at application': SystemTranslationKeys.cart.chargeTiming.atApplication,
    'at move-in': SystemTranslationKeys.cart.chargeTiming.atMoveIn,
    'at move-out': SystemTranslationKeys.cart.chargeTiming.atMoveOut,
    monthly: SystemTranslationKeys.cart.chargeTiming.monthly,
    occasional: SystemTranslationKeys.cart.chargeTiming.occasional,
    'per lease': SystemTranslationKeys.cart.chargeTiming.perLease,
    recurring: SystemTranslationKeys.cart.chargeTiming.recurring,
  },
  method: {
    'per applicant': SystemTranslationKeys.cart.chargeMethod.perApplicant,
    'per unit': SystemTranslationKeys.cart.chargeMethod.perUnit,
    'per occurrence': SystemTranslationKeys.cart.chargeMethod.perOccurrence,
    'per rentable item': SystemTranslationKeys.cart.chargeMethod.perRentableItem,
    'per lease holder': SystemTranslationKeys.cart.chargeMethod.perLeaseHolder,
    'per resident': SystemTranslationKeys.cart.chargeMethod.perResident,
    'per vehicle': SystemTranslationKeys.cart.chargeMethod.perVehicle,
    'per pet': SystemTranslationKeys.cart.chargeMethod.perPet,
  },
  requirement: {
    mandatory: SystemTranslationKeys.cart.requirement.mandatory,
    required: SystemTranslationKeys.cart.requirement.required,
    optional: SystemTranslationKeys.cart.requirement.optional,
    situational: SystemTranslationKeys.cart.requirement.situational,
  },
};

const LEASE_CHARGE_FREQUENCY_TRANSLATION_KEYS: Partial<Record<LeaseChargeType, string>> = {
  [LeaseChargeType.Hourly]: SystemTranslationKeys.cart.chargeFrequency.hourly,
  [LeaseChargeType.Daily]: SystemTranslationKeys.cart.chargeFrequency.daily,
  [LeaseChargeType.Weekly]: SystemTranslationKeys.cart.chargeFrequency.weekly,
  [LeaseChargeType.EveryTwoWeeks]: SystemTranslationKeys.cart.chargeFrequency.everyTwoWeeks,
  [LeaseChargeType.TwicePerMonth]: SystemTranslationKeys.cart.chargeFrequency.twicePerMonth,
  [LeaseChargeType.SpecificLeaseMonths]:
    SystemTranslationKeys.cart.chargeFrequency.specificLeaseMonths,
  [LeaseChargeType.Monthly]: SystemTranslationKeys.cart.chargeFrequency.monthly,
  [LeaseChargeType.Quarterly]: SystemTranslationKeys.cart.chargeFrequency.quarterly,
  [LeaseChargeType.TwicePerYear]: SystemTranslationKeys.cart.chargeFrequency.twicePerYear,
  [LeaseChargeType.Yearly]: SystemTranslationKeys.cart.chargeFrequency.yearly,
  [LeaseChargeType.PercentOfSales]: SystemTranslationKeys.cart.chargeFrequency.percentOfSales,
  [LeaseChargeType.Nightly]: SystemTranslationKeys.cart.chargeFrequency.nightly,
};

function normalizeChargeText(value: string): string {
  return value.trim().toLowerCase().replace(/[–—]/g, '-');
}

export function translateChargeDetailText(
  translate: (key: string) => string,
  type: ChargeDetailTextType,
  value?: string | null,
): string | undefined {
  if (!value) return undefined;
  const key = CHARGE_DETAIL_TRANSLATION_KEYS[type][normalizeChargeText(value)];
  return key ? translate(key) : value;
}

export function getLeaseChargeFrequencyTranslationKey(triggerId?: number): string | undefined {
  return triggerId === undefined
    ? undefined
    : LEASE_CHARGE_FREQUENCY_TRANSLATION_KEYS[triggerId as LeaseChargeType];
}

export interface RangeTotal {
  isRange: boolean;
  minTotal: number;
  maxTotal: number;
  minTotalFormatted: string;
  maxTotalFormatted: string;
  totalFormatted: string;
  currencyCode?: string;
  hasVaries: boolean;
}

/**
 * Appends a per-charge frequency label (e.g. "Per Month", "One-Time") to an
 * already-formatted amount, matching the inline frequency display used across
 * the cart. Returns the amount unchanged when no frequency is provided.
 */
export function appendFrequency(amount: string, frequency?: string): string {
  const trimmedFrequency = frequency?.trim();
  return trimmedFrequency ? `${amount} ${trimmedFrequency}` : amount;
}

/**
 * Composes the display string for a non-numeric custom fee, mirroring the
 * Prospect Portal CustomFeesMapper::formatFeeAmount for the two arms LJ needs:
 * "Varies by Usage Text" -> a localized label, "Custom Text" -> its detail.
 * Returns `undefined` for numeric/range types so callers fall back to their
 * existing currency formatting.
 */
export function formatCustomFeeAmount({
  amountType,
  amountDetail,
  frequency,
  variesByUsageLabel,
}: {
  amountType?: CustomFeeAmountType;
  amountDetail?: string | null;
  frequency?: string;
  variesByUsageLabel: string;
}): string | undefined {
  let baseText: string | undefined;
  switch (amountType) {
    case CustomFeeAmountType.VariesByUsageText:
      baseText = variesByUsageLabel;
      break;
    case CustomFeeAmountType.CustomText:
      baseText = amountDetail ?? undefined;
      break;
    default:
      return undefined;
  }

  // An empty custom-text detail has no displayable amount; return undefined so
  // callers fall back (CartItem -> localized "Varies", ChargesDueCard -> its
  // formatted amount) instead of rendering a blank value behind the dotted leader.
  if (!baseText) {
    return undefined;
  }
  return appendFrequency(baseText, frequency);
}

/**
 * Appends a localized "- Varies" suffix to a total when any contributing
 * charge has a varying (non-numeric) custom-fee amount.
 */
export function formatTotalWithVaries(
  totalFormatted: string,
  hasVaries: boolean,
  variesLabel: string,
): string {
  return hasVaries ? `${totalFormatted} - ${variesLabel}` : totalFormatted;
}

export const filterCustomFees = (charges: CartCharge[]): CartCharge[] => {
  return charges.filter(
    (charge) => typeof charge.id !== 'string' || !charge.id.startsWith('CUSTOM_FEE_'),
  );
};

type ChargeTranslator = (key: string, values?: Record<string, number>) => string;

/** Applies an AR-derived installment balance without mutating its scheduled-charge amount. */
export const getDueNowCharges = (
  charges: CartCharge[],
  translate?: ChargeTranslator,
): CartCharge[] => {
  return charges
    .filter((charge) =>
      isInstallmentCharge(charge)
        ? charge.dueNowAmount !== undefined && charge.dueNowAmount !== 0
        : charge.dueNow,
    )
    .map((charge) => {
      if (charge.dueNowAmount === undefined) return charge;

      const installmentLabel =
        translate && charge.installmentNumber
          ? translate(SystemTranslationKeys.payment.installment, {
              number: charge.installmentNumber,
            })
          : undefined;

      return {
        ...charge,
        label: installmentLabel ? `${installmentLabel} — ${charge.label}` : charge.label,
        arCodeName: undefined,
        arTriggerId: undefined,
        recurringFrequency: undefined,
        allChargesDetails: charge.allChargesDetails?.map((detail) => ({
          ...detail,
          frequency: '',
        })),
        priceNumber: charge.dueNowAmount,
        priceFormatted: undefined,
        taxAmount: undefined,
        isRange: false,
        minPriceNumber: undefined,
        maxPriceNumber: undefined,
        minPriceFormatted: undefined,
        maxPriceFormatted: undefined,
      };
    });
};

/**
 * Returns charges that are payable during the current payment step.
 * Custom fees are displayed separately and are not included in the payment
 * amount, even when their charge metadata marks them as due now.
 */
export const getDueNowPayableCharges = (
  charges: CartCharge[],
  translate?: ChargeTranslator,
): CartCharge[] => {
  return getDueNowCharges(filterCustomFees(charges), translate);
};

/** Uses the same net amount (including credits, taxes and ranges) sent to payment services. */
export function getDueNowPaymentTotal(charges: CartCharge[], currencyCode: string): number {
  return calculateChargeTotal(getDueNowPayableCharges(charges), currencyCode).minTotal;
}

export function hasDueNowPaymentRequired(charges: CartCharge[], currencyCode: string): boolean {
  return isPositiveAmount(getDueNowPaymentTotal(charges, currencyCode));
}

/**
 * Calculate total for an array of charges, handling ranges
 * @param charges Array of charges to sum
 * @returns RangeTotal object with min/max totals if any charge is a range, or single total otherwise
 */
export function calculateChargeTotal(charges: CartCharge[], currencyCode: string): RangeTotal {
  if (!charges || charges.length === 0) {
    const emptyTotal = formatCurrency(0, currencyCode);
    return {
      isRange: false,
      minTotal: 0,
      maxTotal: 0,
      minTotalFormatted: emptyTotal,
      maxTotalFormatted: emptyTotal,
      totalFormatted: emptyTotal,
      currencyCode,
      hasVaries: false,
    };
  }

  const hasRange = charges.some((charge) => charge.isRange === true);
  const hasVaries = charges.some((charge) => charge.amountType === AmountType.VariesAmount);

  const cartChargeTaxAmountNumber = (charge: CartCharge): number => {
    const n = charge?.taxAmount?.amount;
    return typeof n === 'number' && !Number.isNaN(n) ? n : 0;
  };

  if (hasRange) {
    // Calculate min and max totals separately (base + tax per line)
    const minTotal = charges.reduce((sum, charge) => {
      const tax = cartChargeTaxAmountNumber(charge);
      if (charge.isRange && charge.minPriceNumber !== undefined) {
        return sum + charge.minPriceNumber + tax;
      }
      return sum + (charge.priceNumber || 0) + tax;
    }, 0);

    const maxTotal = charges.reduce((sum, charge) => {
      const tax = cartChargeTaxAmountNumber(charge);
      if (charge.isRange && charge.maxPriceNumber !== undefined) {
        return sum + charge.maxPriceNumber + tax;
      }
      return sum + (charge.priceNumber || 0) + tax;
    }, 0);

    const minFormatted = formatCurrency(minTotal, currencyCode);
    const maxFormatted = formatCurrency(maxTotal, currencyCode);

    return {
      isRange: true,
      minTotal,
      maxTotal,
      minTotalFormatted: minFormatted,
      maxTotalFormatted: maxFormatted,
      totalFormatted: `${minFormatted}–${maxFormatted}`,
      currencyCode,
      hasVaries,
    };
  }

  // Single total (no ranges): subtotal + tax per line
  const total = charges.reduce(
    (sum, charge) => sum + (charge.priceNumber || 0) + cartChargeTaxAmountNumber(charge),
    0,
  );
  const formatted = formatCurrency(total, currencyCode);

  return {
    isRange: false,
    minTotal: total,
    maxTotal: total,
    minTotalFormatted: formatted,
    maxTotalFormatted: formatted,
    totalFormatted: formatted,
    currencyCode,
    hasVaries,
  };
}
